import {
  TcmEncounterResponse,
  tcmEncounterResponseSchema,
  UpdateTcmEncounterResponse,
  updateTcmEncounterResponseSchema,
} from "@metriport/shared-internal";
import apiOSS from "../../../api/apiOSS";
import { GetTcmEncounterQueryParams } from "./filters";
import { OutreachStatus } from "./tcm-mutations";

const TCM_ENCOUNTER_URL = `/medical/v1/tcm/encounter`;

const createSortString = (params: GetTcmEncounterQueryParams) => {
  return [
    params.admitTimeSort ? `admitTime=${params.admitTimeSort}` : "",
    params.dischargeTimeSort ? `dischargeTime=${params.dischargeTimeSort}` : "",
    params.lastOutreachDateSort ? `lastOutreachDate=${params.lastOutreachDateSort}` : "",
    params.patientFirstNameSort ? `patientFirstName=${params.patientFirstNameSort}` : "",
    params.patientLastNameSort ? `patientLastName=${params.patientLastNameSort}` : "",
  ]
    .filter(Boolean)
    .join(",");
};

export const GET_TCM_ENCOUNTER_DEFAULT_COUNT = 50;
export const getTcmEncounters = async (
  pageUrl?: string,
  queryParams?: GetTcmEncounterQueryParams
): Promise<TcmEncounterResponse> => {
  const sort = createSortString(queryParams ?? {});
  const response = pageUrl
    ? await apiOSS.get(pageUrl)
    : await apiOSS.get(TCM_ENCOUNTER_URL, {
        params: {
          count: GET_TCM_ENCOUNTER_DEFAULT_COUNT,
          ...(sort ? { sort } : {}),
          ...(queryParams?.facility ? { facilityId: queryParams.facility } : {}),
          ...(queryParams?.days ? { daysLookback: queryParams.days } : {}),
          ...(queryParams?.type ? { eventType: queryParams.type } : {}),
          ...(queryParams?.coding ? { coding: queryParams.coding } : {}),
          ...(queryParams?.status ? { status: queryParams.status } : {}),
          ...(queryParams?.class && queryParams.class.length > 0
            ? { encounterClass: queryParams.class }
            : {}),
          ...(queryParams?.search ? { search: queryParams.search } : {}),
        },
      });
  const parsedResponse = tcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const putTcmEncounterNote = async (
  encounterId: string,
  note: string
): Promise<UpdateTcmEncounterResponse> => {
  const response = await apiOSS.put(`${TCM_ENCOUNTER_URL}/${encounterId}`, {
    freetextNote: note,
  });
  const parsedResponse = updateTcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const putTcmOutreachStatus = async (
  encounterId: string,
  outreachStatus: OutreachStatus
): Promise<UpdateTcmEncounterResponse> => {
  const response = await apiOSS.put(`${TCM_ENCOUNTER_URL}/${encounterId}`, {
    outreachStatus,
    lastOutreachDate: outreachStatus !== "Not Started" ? new Date().toISOString() : null,
  });
  const parsedResponse = updateTcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const putTcmOutreachLog = async (
  encounterId: string,
  outreachLogs: { status: "Attempted" | "Completed"; timestamp: string }[],
  outreachStatus: OutreachStatus
): Promise<UpdateTcmEncounterResponse> => {
  const response = await apiOSS.put(`${TCM_ENCOUNTER_URL}/${encounterId}`, {
    outreachLogs,
    outreachStatus,
    lastOutreachDate: new Date().toISOString(),
  });
  const parsedResponse = updateTcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const putTcmDischargeSummaryPath = async (
  encounterId: string,
  filePath: string
): Promise<UpdateTcmEncounterResponse> => {
  const response = await apiOSS.put(`${TCM_ENCOUNTER_URL}/${encounterId}`, {
    dischargeSummaryPath: filePath,
  });
  const parsedResponse = updateTcmEncounterResponseSchema.parse(response.data);
  return parsedResponse;
};

export const updateTcmEncounterNote = async (
  encounterId: string,
  note: string
): Promise<UpdateTcmEncounterResponse> => {
  const response = await putTcmEncounterNote(encounterId, note);
  return response;
};
